import ImportUploadFilesTemplateFactory from '../../questiontypes/ImportFactories/ImportUploadFilesTemplateFactory';
import errorTemplate from '../../templates/global/livechat_error_template.ejs';
import GlobalVariables from '../GlobalVariables';
import RunAsSoonAs from './RunAsSoonAs';

const TRIGGER_ELEMENT = 'livechat_file_upload_input';
const TRIGGER_WRAPPER = 'livechat_send_attachments_button';
const PREVIEW_CONTAINER = 'upload_files_preview_container';
const FILES_CLASS = 'livechat_available_files';
const ERROR_CLASS = 'files-error';
const ERROR_ELEMENT_CLASS = 'livechat_error_template';
const SUCCESS_EMITTER = 'upload:success';
const ERROR_EMITTER = 'upload:error';

export default class FileUploadProvider {
    constructor(options) {
        this.maxFiles = options.maxFiles || 5;
        this.maxSizeMB = options.maxSizeMB || 5;
        this.allowedTypes = options.allowedTypes || [
            'jpg',
            'jpeg',
            'png',
            'doc',
            'docx',
            'xls',
            'xlsx',
            'pdf'
        ];

        this.DOM = options.DOM;
        this.errorTemplate = errorTemplate;
        this.uploadFilesTemplateFactory = ImportUploadFilesTemplateFactory();
        this.uploadInProgressTemplate = this.uploadFilesTemplateFactory.uploadFilesTemplate;
        this.uploadedFilesTemplate = this.uploadFilesTemplateFactory.questionTypeFilesTemplate;
        this.emitter = options.emitter;

        this.uploadedFilesMap = new Map();
    }

    initUploadTrigger(triggerSelector) {
        const trigger = this.DOM.getElementById(triggerSelector);
        const fileInput = this.DOM.getElementById(TRIGGER_ELEMENT);
        if (!trigger || !fileInput || fileInput.dataset.listenerAttached) return;

        fileInput.addEventListener('change', (e) => this.handleFileInput(e));
        fileInput.dataset.listenerAttached = true; // mark as initialized
    }

    handleFileInput(event) {
        const me = this;
        const input = event.target;
        const files = Array.from(input.files);
        const trigger = input.closest(`#${TRIGGER_WRAPPER}`);
        FileUploadProvider.clearError(trigger);

        const uploadWrapper = this.DOM.getElementById(PREVIEW_CONTAINER);
        if (!uploadWrapper) return;
        if (!me.newValidFiles) {
            me.newValidFiles = [];
        }
        files.some((file) => {
            const fileKey = `${file.name}_${file.size}`;

            if (this.uploadedFilesMap.has(fileKey)) return false;

            if (this.uploadedFilesMap.size + me.newValidFiles.length >= this.maxFiles) {
                this.showError(trigger, `Maximum ${this.maxFiles} file(s) allowed.`);
                return true; // break
            }

            const ext = file.name.split('.').pop().toLowerCase();
            const sizeMB = file.size / (1024 * 1024);

            if (!this.allowedTypes.includes(ext)) {
                this.showError(trigger, `File type '.${ext}' is not allowed.`);
                return false;
            }

            if (sizeMB > this.maxSizeMB) {
                this.showError(trigger, `Each file must be smaller than ${this.maxSizeMB}MB.`);
                return false;
            }

            this.uploadedFilesMap.set(fileKey, true);
            me.newValidFiles.push(file);
            return false;
        });
        uploadWrapper.innerHTML = '';
        this.tempPreviewFiles = [];
        me.newValidFiles.forEach((file) => {
            this.readAndUploadFile(file, uploadWrapper);
        });
        input.value = '';
    }

    async readAndUploadFile(file, container) {
        const ext = file.name.split('.').pop().toLowerCase();
        const safeName = file.name.replace(/[^a-zA-Z0-9-_]/g, '_');
        const fileId = crypto.randomUUID();

        // Create preview immediately (status = uploading)
        const fileData = {
            id: fileId,
            type: ext,
            name: safeName,
            size: file.size,
            extension: ext,
            status: 'uploading',
            url: URL.createObjectURL(file) // local preview
        };

        const html = this.uploadInProgressTemplate(fileData);
        container.insertAdjacentHTML('beforeend', html);
        container.classList.add(FILES_CLASS);
        this.attachRemoveHandler(file);
        if (!this.tempPreviewFiles) this.tempPreviewFiles = [];
        this.tempPreviewFiles.push(fileData);
    }

    async processUpload(file, chatLogId) {
        // Prepare FormData for server
        const formData = new FormData();
        formData.append('file', file, file.name);
        try {
            const response = await fetch(
                `${GlobalVariables.LiveChatServiceUrl}Services/ApexChatUtilityService.svc/chatlogs/${chatLogId}/upload-visitor-attached-file`,
                {
                    method: 'POST',
                    body: formData
                }
            );
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const result = await response.json();

            // Update preview status
            const fileObject = Object.assign(file, { status: 'success' });
            this.emitter.emit(SUCCESS_EMITTER, { fileObject, uploaded: result.data });
            return result.data;

        } catch (error) {
            const fileObject = Object.assign(file, { status: 'failed' });
            this.emitter.emit(ERROR_EMITTER, error.message);
            return fileObject;
        } finally {
            // Attach remove handler
            // this.attachRemoveHandler(file);
        }
    }

    async uploadFiles(message) {
        if (!message.id) return;
        // Map files → promises
        const uploadPromises = this.newValidFiles.map((file) =>
            this.processUpload(file, message.id)
        );

        try {
            // Wait for all uploads
            const results = await Promise.all(uploadPromises);

            // Push results into previewFiles
            if (!this.previewFiles) this.previewFiles = [];
            this.previewFiles.push(...results);

            // Trigger after all done
            this.showFilesInMessage(message.id);
        } catch (err) {
            console.error('Some uploads failed:', err);
            // Optional: handle partial results with Promise.allSettled
        }
    }

    static findNextElement(filesElement) {
        if (!filesElement) return null;

        let el = filesElement.nextElementSibling;
        while (el) {
            if (el.matches('.apexchat_message_right')) {
                return el;
            }
            el = el.nextElementSibling;
        }

        return null; // not found
    }

    static formatFileSize(bytes) {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
        if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }

    showFilesInMessage(chatlogId, data = []) {
        if (!chatlogId) return;
        const me = this;
        let attamentsContainer = null;
        RunAsSoonAs(
            () => {
                if (attamentsContainer) {
                    let files = [];
                    if (data.length > 0) {
                        files = data;
                    } else if (me.previewFiles.length > 0) {
                        files = me.previewFiles;
                    }
                    attamentsContainer.innerHTML = '';
                    files.forEach((file) => {
                        const fileObject = {
                            ...file,
                            messageId: chatlogId,
                            styles: '',
                            fontStyle: '',
                            isUnviewable: '',
                            url: file.path,
                            type: file.fileExtension,
                            name: file.fileName,
                            size: FileUploadProvider.formatFileSize(file.fileSize)
                        };
                        attamentsContainer.innerHTML += this.uploadedFilesTemplate(fileObject);
                    });
                }
            },
            () => {
                attamentsContainer = me.DOM.getElementById(
                    `apexchat_visitors_attachments_div_${chatlogId}`
                );
                return !!attamentsContainer;
            }
        );
    }

    showTempFilesInMessage(message) {
        if (!message.chatLogId) return;
        const me = this;
        let visitorResponse = null;
        RunAsSoonAs(
            () => {
                if (visitorResponse) {
                    const filesElement = visitorResponse.querySelector(`.livechat_files_container`);
                    filesElement.innerHTML = '';
                    me.tempPreviewFiles.forEach((file) => {
                        const fileObject = Object.assign(file, {
                            ...file,
                            messageId: message.chatLogId,
                            styles: '',
                            fontStyle: '',
                            isUnviewable: ''
                        });
                        filesElement.innerHTML += this.uploadedFilesTemplate(fileObject);
                    });
                }
            },
            () => {
                const questionElement = me.DOM.querySelector(`[chatlogid='${message.chatLogId}']`);
                visitorResponse = FileUploadProvider.findNextElement(questionElement);
                return !!questionElement && !!visitorResponse;
            }
        );
    }

    attachRemoveHandler(file) {
        const fileKey = `${file.name}_${file.size}`;
        const safeName = file.name.replace(/[^a-zA-Z0-9-_]/g, '_'); // consistent with ID
        const previewId = `livechat_uploaded_file_${safeName}`;
        const removeBtnClass = `.remove_file_${safeName}`;
        const previewEl = this.DOM.getElementById(previewId);
        if (!previewEl) return;

        const removeBtn = previewEl.querySelector(removeBtnClass);

        if (removeBtn) {
            removeBtn.addEventListener('click', () => {
                // Remove from Map
                this.uploadedFilesMap.delete(fileKey);

                // Remove DOM element
                previewEl.remove();

                // Remove class if no files left
                const container = this.DOM.getElementById(PREVIEW_CONTAINER);
                if (container && container.children.length === 0) {
                    container.classList.remove(FILES_CLASS);
                }

                // Optional: emit event
                this.emitter.emit('upload:removed', fileKey);
            });
        }
    }

    showError(trigger, message) {
        setTimeout(() => {
            const errorHTML = this.errorTemplate({
                error: message,
                type: ERROR_CLASS
            });
            trigger.insertAdjacentHTML('beforeend', errorHTML);
            this.emitter.emit(ERROR_EMITTER, message);
            const errorEl = this.DOM.querySelector(`.${ERROR_ELEMENT_CLASS}`);
            if (errorEl) {
                setTimeout(() => {
                    errorEl.remove();
                }, 3000);
                errorEl.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    errorEl.remove();
                });
            }
        }, 10);
    }

    static clearError(trigger) {
        const existingError = trigger.querySelector(`.${ERROR_CLASS}`);
        if (existingError) {
            existingError.remove();
        }
    }

    clearUploads(context) {
        if (this.uploadedFilesMap && this.uploadedFilesMap.clear) {
            this.uploadedFilesMap.clear();
        }
        const uploadWrapper = context.getElementById(PREVIEW_CONTAINER);
        if (uploadWrapper) {
            uploadWrapper.innerHTML = '';
            uploadWrapper.classList.remove(FILES_CLASS);
        }
        const fileInput = this.DOM.getElementById(TRIGGER_ELEMENT);
        if (fileInput) {
            fileInput.value = '';
        }
    }
}
